import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, FileTextIcon } from 'lucide-react';
import { Button, Skeleton } from '@opaz/ui-components';
import { cn } from '@/lib/utils';
import { Languages } from '@/lib/constants';
import { useNavigate } from '@tanstack/react-router';

interface ServiceItem {
  documentId: string;
  title: string;
  description: string;
}

interface Props {
  services: ServiceItem[];
  isLoading: boolean;
}

const CARD_WIDTH_DESKTOP = 304;
const CARD_GAP_DESKTOP = 24;
const SCROLL_STEP = CARD_WIDTH_DESKTOP + CARD_GAP_DESKTOP;
const CLONE_COUNT = 3;
const AUTO_SCROLL_INTERVAL = 3000;

export default function FeaturedServicesCarousel({ services, isLoading }: Props) {
  const { t, i18n } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isJumpingRef = useRef(false);
  const isRTL = i18n.language === Languages.AR;
  const navigate = useNavigate();
  const intervalRef = useRef<number | null>(null);

  const clonedServices = useMemo(() => {
    if (!services || services.length <= CLONE_COUNT) {
      return services;
    }
    const startClones = services.slice(0, CLONE_COUNT);
    const endClones = services.slice(-CLONE_COUNT);
    return [...endClones, ...services, ...startClones];
  }, [services]);

  const getNormalizedScrollLeft = useCallback(
    (element: HTMLElement) => {
      if (!element) return 0;
      const { scrollLeft, scrollWidth, clientWidth } = element;

      if (isRTL) {
        if (scrollLeft < 0) {
          return scrollWidth - clientWidth + scrollLeft;
        }
        return scrollLeft;
      }
      return scrollLeft;
    },
    [isRTL],
  );

  const setScrollLeft = useCallback(
    (element: HTMLElement, value: number, behavior: ScrollBehavior = 'smooth') => {
      if (!element) return;
      if (isRTL) {
        element.scrollTo({ left: value, behavior });
      } else {
        element.scrollTo({ left: value, behavior });
      }
    },
    [isRTL],
  );

  const scrollBy = useCallback(
    (step: number, behavior: ScrollBehavior = 'smooth') => {
      if (!scrollRef.current) return;

      if (isRTL) {
        scrollRef.current.scrollBy({
          left: -step,
          behavior,
        });
      } else {
        scrollRef.current.scrollBy({
          left: step,
          behavior,
        });
      }
    },
    [isRTL],
  );

  const handleInfiniteScroll = useCallback(() => {
    if (isJumpingRef.current) return;
    const el = scrollRef.current;
    if (!el) return;

    const normalizedScrollLeft = getNormalizedScrollLeft(el);
    const originalServicesLength = services.length;

    const originalContentStart = CLONE_COUNT * SCROLL_STEP;
    const originalContentEnd = (CLONE_COUNT + originalServicesLength) * SCROLL_STEP;

    if (normalizedScrollLeft >= originalContentEnd) {
      isJumpingRef.current = true;
      const newScrollLeft = originalContentStart + (normalizedScrollLeft - originalContentEnd);
      setScrollLeft(el, newScrollLeft, 'instant');
    } else if (normalizedScrollLeft < originalContentStart) {
      isJumpingRef.current = true;
      const newScrollLeft = originalContentEnd - (originalContentStart - normalizedScrollLeft);
      setScrollLeft(el, newScrollLeft, 'instant');
    }

    setTimeout(() => {
      isJumpingRef.current = false;
    }, 50);
  }, [services.length, getNormalizedScrollLeft, setScrollLeft]);

  const startAutoScroll = useCallback(() => {
    if (services.length <= CLONE_COUNT) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = window.setInterval(() => {
      scrollBy(SCROLL_STEP);
    }, AUTO_SCROLL_INTERVAL);
  }, [services.length, scrollBy]);

  const stopAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || services.length <= CLONE_COUNT) return;

    const initialScrollPosition = CLONE_COUNT * SCROLL_STEP;
    setScrollLeft(el, initialScrollPosition, 'instant');
  }, [services, setScrollLeft]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || services.length <= CLONE_COUNT) return;

    el.addEventListener('scroll', handleInfiniteScroll);

    startAutoScroll();

    return () => {
      el.removeEventListener('scroll', handleInfiniteScroll);
      stopAutoScroll();
    };
  }, [handleInfiniteScroll, services.length, startAutoScroll, stopAutoScroll]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || services.length <= CLONE_COUNT) return;

    el.addEventListener('mouseenter', stopAutoScroll);
    el.addEventListener('mouseleave', startAutoScroll);

    return () => {
      el.removeEventListener('mouseenter', stopAutoScroll);
      el.removeEventListener('mouseleave', startAutoScroll);
    };
  }, [services.length, startAutoScroll, stopAutoScroll]);

  const carouselButtonStyles =
    'w-12 h-12 rounded-full flex items-center justify-center border-2 transition-colors border-[#1E2B3B] text-[#1E2B3B] hover:bg-gray-100 active:bg-gray-200';

  if (isLoading) {
    return (
      <div className="w-full grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ">
        {Array.from({ length: 3 }).map((_, idx) => (
          <Skeleton key={idx} className="h-80 rounded bg-white w-full" />
        ))}
        <div role="status" aria-live="polite" className="sr-only">
          {t('home.loadingFeaturedServices')}
        </div>
      </div>
    );
  }

  if (!services || services.length === 0) return null;

  const isScrollable = services.length > 1;

  return (
    <div className="w-full space-y-12">
      <div className="flex flex-wrap justify-between items-center gap-4 md:gap-2 px-4 md:px-0">
        <h2 className="flex flex-col text-3xl md:text-4xl font-normal text-[#1A444F]">
          <span>{t('home.yourGateway')}</span>
          <span className="font-bold">{t('home.investmentSuccess')}</span>
        </h2>
        {isScrollable && (
          <div className={cn('hidden md:flex gap-2', isRTL && 'flex-row-reverse')}>
            <button
              onClick={() => {
                stopAutoScroll();
                scrollBy(-SCROLL_STEP);
                startAutoScroll();
              }}
              className={carouselButtonStyles}
              aria-label={t('home.scrollLeft')}
            >
              <ArrowLeft />
            </button>

            <button
              onClick={() => {
                stopAutoScroll();
                scrollBy(SCROLL_STEP);
                startAutoScroll();
              }}
              className={carouselButtonStyles}
              aria-label={t('home.scrollRight')}
            >
              <ArrowRight />
            </button>
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        id="services-scroll"
        className={cn(
          'flex gap-4 md:gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory hide-scrollbar pb-6 px-4 md:px-0 md:mx-0',
          isRTL ? 'direction-rtl' : 'direction-ltr',
        )}
      >
        {(isScrollable ? clonedServices : services).map((service, index) => (
          <div
            key={`${service.documentId}-${index}`}
            className="w-[90vw] sm:w-80 md:w-[304px] snap-start shrink-0 rounded-xl bg-white shadow flex flex-col justify-between p-6 gap-4"
          >
            <div className="flex flex-col gap-4">
              <div className="h-12 w-12 mb-4 bg-[#ECEEF4] rounded-full flex items-center justify-center">
                <FileTextIcon className="h-6 w-6 text-[#003657]" />
              </div>
              <h3 className="text-lg font-bold text-[#27272A]">{service.title}</h3>
              <p className="text-sm text-[#27272A]">{service.description}</p>
            </div>
            <Button
              variant="secondary"
              onClick={() => {
                navigate({
                  to: `/services/services-catalogue/${service.documentId}`,
                });
              }}
              className="h-10 w-full rounded-full border border-[#1E2B3B] text-[#1E2B3B]"
            >
              {t('home.startService')}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
